import FileInput from '@/components/ui/file-input';
import { FileStatus } from '@/components/ui/file-uploaded/types';
import FileUploaded from '@/components/ui/file-uploaded';
import FormDialog from '@/components/ui/form-dialog';
import { Stack } from '@mui/system';
import { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { uploadTemplate } from '@/api/sites';
import { useFileUpload } from '@/shared/hooks/useFileUpload';
import useToastMessage from '@/shared/context/toast-message-context';
import { AxiosError } from 'axios';
import { ApiError } from '@/shared/types/api-errors';

interface UploadLocationsDialogProps {
	open: boolean;
	onClose: () => void;
	siteId?: string;
	onSubmitted?: () => Promise<void> | void;
}

const UploadLocationsDialog: FunctionComponent<UploadLocationsDialogProps> = ({
	open,
	onClose,
	siteId,
	onSubmitted,
}): ReactElement => {
	const [error, setError] = useState<string>();
	const { fileInputRef, file, status, onFileChange, onFileRemoved, setFile, setStatus } =
		useFileUpload();
	const { dispatch } = useToastMessage();

	useEffect(() => {
		if (!open) {
			setFile(undefined);
			setStatus(undefined);
			setError(undefined);
		}
	}, [open, setFile, setStatus]);

	const { isLoading, mutate } = useMutation({
		mutationFn: (formData: FormData) => uploadTemplate(siteId!, formData),
		onSuccess: async () => {
			await onSubmitted?.();
			onClose();
			dispatch({
				type: 'success',
				content: 'Locations successfully updated',
			});
		},
		onError: (error: AxiosError<ApiError>) => {
			const { statusCode, message } = error.response?.data || {};
			if (statusCode === 400) {
				dispatch({ type: 'error', content: message });
				setError(message);
			}
		},
	});

	const onSubmit = () => {
		if (!file) return;
		const formData = new FormData();
		formData.append('file', file);

		mutate(formData);
	};

	return (
		<FormDialog
			open={open}
			onClose={onClose}
			title="Upload locations CSV"
			maxWidth="sm"
			fullWidth
			actions={{
				confirm: {
					label: 'Upload locations',
					disabled: !file && status !== FileStatus.LOADED,
					isLoading,
				},
				cancel: { label: 'Cancel' },
			}}
			onSubmit={onSubmit}
		>
			<Stack direction="column" spacing={1}>
				<FileInput
					id="locations"
					name="locations"
					accept=".csv"
					acceptReadable="CSV (max. 3MB)"
					onChange={onFileChange}
					ref={fileInputRef}
					error={error}
				/>
				{file && <FileUploaded file={file} status={status} onRemove={onFileRemoved} />}
			</Stack>
		</FormDialog>
	);
};

export default UploadLocationsDialog;
